import React, {ReactElement, useEffect, useState} from "react";
import {
    Box,
    Button, Dropdown, FormControl, FormLabel,
    Grid, IconButton, Input,
    Menu,
    MenuButton,
    MenuItem,
    Modal,
    ModalClose, Select,
    Sheet,
    Stack, Textarea,
    Tooltip,
    Typography,
    Option, Chip,
    FormHelperText
} from "@mui/joy";
import {PageStandard} from "../utils/PageStandard";
import {useParams} from "react-router-dom";
import {CSS} from "@dnd-kit/utilities";
import {
    AddBox, ArrowDropDown, ArrowDropUp, Delete, DragIndicator,
    EmojiEvents,
    Explore,
    Flag,
    MapsHomeWork, MoreVert,
    PlayCircleFilledWhite,
    PsychologyAlt,
    QrCodeScanner,
    QuestionAnswer,
    Stairs,
    WorkspacePremium,
    ContentCopy,
    Traffic,
    AccountTree
} from "@mui/icons-material";
import {arrayMove, SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {closestCenter, DndContext, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {
    Compass,
    EndGame,
    Flow,
    FlowStep,
    FlowStepTypes,
    GoTo, MediaTypes,
    QrCodeScan, Question,
    Quiz, Ranking,
    Semaforo,
    StartGame, Step,
    Token
} from "../utils/Model";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {getUUID, useUnloadWarning} from "../utils/Utils";
import {MapContainer, TileLayer, Marker, Popup, useMapEvents} from 'react-leaflet'
import {LatLng} from "leaflet";
import {createQrCode, getFlows, getQrCodes, updateFlow } from "../utils/ViewModel";
import {QrCodeCatalogEntry} from "../utils/Model";


export function GameFlow() {
    const { id } = useParams<{ id: string }>();
    const [flows, setFlows] = useState<Flow[]>([]);
    const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
    const [modalNewStep, setModalNewStep] = useState(false);

    const handleUpdateFlow = () => {
        if(id) {
            updateFlow(id, flows)
                .then((success) => {
                    if (success) {
                        alert("Flow aggiornato con successo")
                    }
                })
                .catch((err) => alert("Errore nell’aggiornamento del flow: " + err.message));
        }
    };

    const handleGetFlows = () => {
        if(id) {
            getFlows(id)
                .then((flows) => {
                    if (flows) {
                        setFlows(flows);
                    }
                })
                .catch((err) => alert("Errore nel recupero dei flow: " + err.message));
        }
    };

    useEffect(() => {
        handleGetFlows()
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    useUnloadWarning();

    // Flow attivo corrente
    const activeFlow: Flow | null = flows.find((f) => f.id === activeFlowId)!;

    function handleDragEnd(event: any) {
        const { active, over } = event;
        if (!over) return;

        if (active.id !== over.id && activeFlow !== null && activeFlow !== undefined) {
            const oldIndex = activeFlow.steps.findIndex(
                (item) => item.id === active.id
            );
            const newIndex = activeFlow.steps.findIndex(
                (item) => item.id === over.id
            );

            if (oldIndex !== -1 && newIndex !== -1) {
                if(activeFlowId !== null) {
                    updateFlowSteps(
                        activeFlowId,
                        arrayMove(activeFlow.steps, oldIndex, newIndex)
                    );
                }
            }
        }
    }

    const ModalItems = (Object.values(FlowStepTypes) as FlowStepTypes[])
        .map((v) => createStepFlow(v));

    const deleteStep = (step: FlowStep) => {
        if(activeFlowId !== null) {
            updateFlowSteps(
                activeFlowId,
                activeFlow.steps.filter((s) => s.id !== step.id)
            );
        }
    };

    const duplicateStep = (step: FlowStep) => {
        if(activeFlowId !== null) {
            const index = activeFlow.steps.findIndex((s) => s.id === step.id);
            const duplicated: FlowStep = { ...step, id: getUUID() };
            const newSteps = [...activeFlow.steps];
            newSteps.splice(index + 1, 0, duplicated);
            updateFlowSteps(activeFlowId, newSteps);
        }
    };

    const handleUpdateStep = (id: string, updatedStep: FlowStep) => {
        if (activeFlowId !== null) {
            updateFlowSteps(
                activeFlowId,
                activeFlow.steps.map((s) => (s.id === id ? updatedStep : s))
            );
        }
    };

    function handleStepSelect(slug: FlowStepTypes) {
        const newitem = createStepFlow(slug);
        if(activeFlowId !== null && activeFlow !== null && activeFlow !== undefined) {
            updateFlowSteps(activeFlowId, [...activeFlow.steps, newitem]);
        }
        setModalNewStep(false);
    }

    function updateFlowSteps(flowId: string, newSteps: FlowStep[]) {
        setFlows((prev) =>
            prev.map((f) => (f.id === flowId ? { ...f, steps: newSteps } : f))
        );
    }

    function updateFlowTitle(flowId: string | null, title: string) {
        if (!flowId) return;
        setFlows(prev => prev.map(f => (f.id === flowId ? { ...f, title } : f)));
    }

    function addFlow() {
        const newFlow: Flow = { id: getUUID(), title: "Nuovo flow", steps: [] };
        setFlows((prev) => [...prev, newFlow]);
        setActiveFlowId(newFlow.id);
    }

    function deleteFlow(flowId: string) {
        if(window.confirm("Sei sicuro di voler eliminare questo flusso?")) {
            setFlows((prev) => prev.filter((f) => f.id !== flowId));
            if (activeFlowId === flowId && flows.length > 1) {
                setActiveFlowId(flows[0].id);
            }
        }
    }

    function duplicateFlow(flowId: string) {
        const flowToDuplicate = flows.find((f) => f.id === flowId);
        if (!flowToDuplicate) return;
        const duplicated: Flow = {
            id: getUUID(),
            title: flowToDuplicate.title + " (copy)",
            steps: [...flowToDuplicate.steps.map((s) => ({ ...s, id: getUUID() }))],
        };
        setFlows((prev) => [...prev, duplicated]);
        setActiveFlowId(duplicated.id);
    }

    return (
        <PageStandard
            title="Flow di gioco"
            sidebarVisible={true}
            headerEnd={
                <Typography fontFamily="monospace" level="body-sm">
                    ID: {id}
                </Typography>
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 0,
                    height: 'calc(100vh - 120px)',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'lg',
                }}
            >
                {/* ── Sinistra: lista dei flow ─────────────────────────────── */}
                <Sheet
                    sx={{
                        width: 320,
                        minWidth: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Button fullWidth size="sm" startDecorator={<AddBox />} onClick={addFlow}>
                            Nuovo flow
                        </Button>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                        {flows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <AccountTree sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
                                <Typography level="body-sm" color="neutral">
                                    Nessun flow ancora. Creane uno!
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={0.5}>
                                {flows.map((flow) => (
                                    <Sheet
                                        key={flow.id}
                                        variant={flow.id === activeFlowId ? 'soft' : 'plain'}
                                        color={flow.id === activeFlowId ? 'primary' : 'neutral'}
                                        sx={{
                                            borderRadius: 'sm',
                                            p: 1,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            '&:hover': { bgcolor: flow.id === activeFlowId ? undefined : 'background.level1' },
                                        }}
                                        onClick={() => setActiveFlowId(flow.id)}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                level="body-sm"
                                                fontWeight={flow.id === activeFlowId ? 'bold' : 'normal'}
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {flow.title || <em style={{ color: 'var(--joy-palette-text-tertiary)' }}>Senza titolo</em>}
                                            </Typography>
                                            <Typography level="body-xs" color="neutral">
                                                {flow.steps.length} step{flow.steps.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.25} onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="Duplica">
                                                <IconButton size="sm" variant="plain" onClick={() => duplicateFlow(flow.id)}>
                                                    <ContentCopy sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Elimina">
                                                <IconButton size="sm" variant="plain" color="danger" onClick={() => deleteFlow(flow.id)}>
                                                    <Delete sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Sheet>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Sheet>

                {/* ── Destra: editor del flow selezionato ──────────────────── */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {(activeFlow !== null && activeFlow !== undefined) ? (
                        <>
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Titolo flow *</FormLabel>
                                    <Input
                                        type="text"
                                        value={activeFlow.title}
                                        onChange={(e) => updateFlowTitle(activeFlowId, (e.target as HTMLInputElement).value)}
                                    />
                                </FormControl>
                                <Button sx={{ mt: 3 }} onClick={handleUpdateFlow}>
                                    Salva modifiche
                                </Button>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                                <Stack direction="column" spacing={2}>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={activeFlow.steps}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            {activeFlow.steps.map((step) => (
                                                <SortableItem key={step.id} id={step.id}>
                                                    {({ attributes, listeners }) =>
                                                        switchStepFlow(
                                                            step,
                                                            () => deleteStep(step),
                                                            (updated) =>
                                                                handleUpdateStep(step.id, updated),
                                                            () => duplicateStep(step),
                                                            { attributes, listeners }
                                                        )
                                                    }
                                                </SortableItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>

                                    {/* Pulsanti di azione sugli step */}
                                    <Stack direction="row" spacing={2} sx={{ pt: 1, pb: 3 }}>
                                        <Button
                                            onClick={() => {
                                                setModalNewStep(true);
                                            }}
                                            sx={{ flexGrow: 1 }}
                                            variant="outlined"
                                            startDecorator={<AddBox />}
                                        >
                                            Aggiungi step
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5, color: 'text.tertiary' }}>
                            <AccountTree sx={{ fontSize: 52 }} />
                            <Typography level="title-md" color="neutral">
                                Seleziona un flow da modificare o creane uno nuovo
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modal per aggiungere uno step */}
            <Modal
                open={modalNewStep}
                onClose={() => setModalNewStep(false)}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Sheet
                    variant="outlined"
                    sx={{
                        maxWidth: 800,
                        borderRadius: "md",
                        p: 3,
                        boxShadow: "lg",
                    }}
                >
                    <ModalClose variant="plain" sx={{ m: 1 }} />
                    <Typography level="h3" mb={3}>
                        Aggiungi step
                    </Typography>
                    <Grid container spacing={1}>
                        {ModalItems.map((a) => {
                            const helper = getHelper(a.type)
                            return (
                                <ItemStepModal
                                    title={helper.title}
                                    subtitle={helper.subtitle}
                                    slug={a.type}
                                    icon={helper.icon}
                                    onClick={() => handleStepSelect(a.type)}
                                    key={a.id}
                                />
                            )
                        })}
                    </Grid>
                </Sheet>
            </Modal>
        </PageStandard>
    );
}

function ItemStepModal(props: {title: string, subtitle: string, slug: FlowStepTypes, icon: ReactElement, onClick: (slug: FlowStepTypes) => void}) {
    return (
        <Grid xs={12} md={6} flexGrow={1}>
            <Sheet color="neutral"
                   variant="outlined"
                   sx={{p:2, borderRadius: 10, height: "100%"}}
                   onClick={() => {
                       props.onClick(props.slug)
                   }}
                   className="cursorPointer hoverScale"
            >
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{height: "100%"}}>
                    <Typography level="h3">
                        {props.icon}
                    </Typography>
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        <Typography level="title-sm" fontWeight="bold">{props.title}</Typography>
                        <Typography level="body-sm">{props.subtitle}</Typography>
                    </Stack>
                </Stack>
            </Sheet>
        </Grid>
    )
}

export function SortableItem({ id, children }: { id: string; children: (props: any) => React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style}>
            {children({ attributes, listeners })}
        </Box>
    );
}

function switchStepFlow(step: FlowStep, onDelete: () => void, onUpdate: (updated: FlowStep) => void, onDuplicate: () => void, p0: {
    attributes: any;
    listeners: any;
}) {
    switch (step.type) {
        case FlowStepTypes.START_GAME:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0} />;
        case FlowStepTypes.END_GAME:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <EndGameFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.QR_CODE_SCAN:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <QrCodeFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.QUIZ:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <QuizFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.TOKEN:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <TokenFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.GO_TO:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <GoToFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.COMPASS:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <CompassFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.RANKING:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <RankingFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.QUESTION:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0}>
                <QuestionFormFlow step={step} onUpdate={onUpdate} />
            </WrapperItemStep>;
        case FlowStepTypes.STEP:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0} />;
        case FlowStepTypes.SEMAFORO:
            return <WrapperItemStep step={step} onDelete={onDelete} onUpdate={onUpdate} onDuplicate={onDuplicate} handleProps={p0} />;
        default:
            return <Typography>Unknown Step</Typography>;
    }
}

function WrapperItemStep({ step, children, onDelete, onUpdate, onDuplicate, handleProps }: {
    step: FlowStep,
    children?: React.ReactNode,
    onDelete?: () => void,
    onUpdate?: (step: FlowStep) => void,
    onDuplicate?: () => void,
    handleProps?: any
}) {
    const [expanded, setExpanded] = useState(false)
    const [expandedHints, setExpandedHints] = useState(false)

    const handleChange = (field: keyof FlowStep, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };

    const helper = getHelper(step.type)
    const summary = getStepSummary(step)
    return (
        <Sheet color="neutral" variant="outlined" sx={{p:2, borderRadius: 10, height: "100%"}}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
                <IconButton
                    {...handleProps.attributes}
                    {...handleProps.listeners}
                    tabIndex={0}
                >
                    <DragIndicator />
                </IconButton>
                <Stack direction="column" spacing={0} flexGrow={1} sx={{minWidth: 0, cursor: 'pointer'}} onClick={() => setExpanded(!expanded)}>
                    <Tooltip title={helper.subtitle} color="neutral" placement="top" variant="outlined" size="sm">
                        <Typography level="title-md" fontWeight="bold" startDecorator={helper.icon}
                                    sx={{display: 'inline-flex', alignItems: 'center'}}
                        >
                            {step.displayTitle || helper.title}
                        </Typography>
                    </Tooltip>
                    <Typography level="body-xs" color="neutral" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pl: '2rem'}}>
                        {summary}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <IconButton variant="outlined" color="neutral" onClick={() => setExpanded(!expanded)}>
                        {
                            expanded ?
                                <ArrowDropUp />
                                :
                                <ArrowDropDown />
                        }
                    </IconButton>
                    <Dropdown>
                        <MenuButton
                            size="sm"
                            onClick={(e) => e.stopPropagation()} // Previene il click del link
                        >
                            <MoreVert />
                        </MenuButton>
                        <Menu size="sm" sx={{zIndex: 999999}}>
                            <MenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDuplicate?.()
                                }}
                            >
                                <ContentCopy fontSize="small" />
                                Duplica
                            </MenuItem>
                            <MenuItem
                                color="danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete?.()
                                }}
                            >
                                <DeleteForeverIcon fontSize="small" />
                                Elimina
                            </MenuItem>
                        </Menu>
                    </Dropdown>
                </Stack>
            </Stack>
            {
                expanded ?
                    <Stack direction="column" spacing={2}>
                        <FormControl size="sm"
                                     error={step?.displayTitle.length < 5 || step?.displayTitle.length > 255}
                        >
                            <FormLabel>Display title</FormLabel>
                            <Input type="text"
                                   placeholder="Titolo"
                                   value={step.displayTitle}
                                   onChange={(e) => handleChange("displayTitle", e.target.value)}
                            />
                            <FormHelperText>Massimo 255 caratteri</FormHelperText>
                        </FormControl>
                        <FormControl size="sm"
                                     error={step?.displayDesc.length < 5 || step?.displayDesc.length > 1000}
                        >
                            <FormLabel>Display description</FormLabel>
                            <Textarea placeholder="Descrizione"
                                      value={step.displayDesc}
                                      onChange={(e) => handleChange("displayDesc", e.target.value)}
                                      minRows={4}
                                      maxRows={4}
                            />
                            <FormHelperText>Massimo 1000 caratteri</FormHelperText>
                        </FormControl>
                        {children}
                        <Typography level="title-md" fontWeight="bold"
                                    onClick={() => setExpandedHints(!expandedHints)}
                                    endDecorator={
                                        expandedHints ?
                                            <ArrowDropUp />
                                            :
                                            <ArrowDropDown />
                                    }
                                    className="cursorPointer"
                                    sx={{pt:3}}
                        >
                            Suggerimenti
                        </Typography>
                        {
                            expandedHints ?
                                <Stack direction="column" spacing={1}>
                                    <FormControl size="sm">
                                        <FormLabel>Delay suggerimenti</FormLabel>
                                        <Input type="number"
                                               value={step.hintsDelay}
                                               onChange={e => handleChange("hintsDelay", e.target.value)}
                                               endDecorator={<Typography>secondi</Typography>}
                                        />
                                    </FormControl>
                                    <FormControl size="sm" error={!Array.isArray(step.hints) || step.hints.length <= 0}>
                                        <FormLabel>
                                            <Typography
                                                level="body-sm"
                                                endDecorator={
                                                    <IconButton
                                                        onClick={() =>
                                                            handleChange(
                                                                "hints",
                                                                [...(step.hints || []), ""]
                                                            )
                                                        }
                                                    >
                                                        <AddBox />
                                                    </IconButton>
                                                }
                                            >
                                                Suggerimenti
                                            </Typography>
                                        </FormLabel>
                                        <Stack direction="column" spacing={1}>
                                            {(Array.isArray(step.hints) ? step.hints : []).map((a, index) => (
                                                <Input
                                                    key={index}
                                                    type="text"
                                                    placeholder="Testo"
                                                    value={a}
                                                    onChange={(e) => {
                                                        const updatedAnswers = [...(step.hints || [])]
                                                        updatedAnswers[index] = e.target.value
                                                        handleChange("hints", updatedAnswers)
                                                    }}
                                                    endDecorator={
                                                        <IconButton
                                                            onClick={() => {
                                                                const updatedAnswers = (step.hints || []).filter((_, i) => i !== index)
                                                                handleChange("hints", updatedAnswers)
                                                            }}
                                                            disabled={step.hints.length <= 1}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    }
                                                />
                                            ))}
                                            <FormControl size="sm" sx={{width: "100%"}}>
                                                <FormLabel>Completare entro</FormLabel>
                                                <Input type="number"
                                                       value={step.completeUntil ?? ""}
                                                       onChange={e => handleChange("completeUntil", e.target.value)}
                                                />
                                            </FormControl>
                                        </Stack>
                                    </FormControl>
                                </Stack>
                                : null
                        }
                    </Stack>
                    : null
            }
        </Sheet>
    )
}

function truncate(text: string, max: number = 80): string {
    const trimmed = (text || "").trim();
    if (trimmed.length === 0) return "";
    return trimmed.length > max ? trimmed.slice(0, max) + "…" : trimmed;
}

function getStepSummary(step: FlowStep): string {
    switch (step.type) {
        case FlowStepTypes.QUIZ:
        case FlowStepTypes.QUESTION:
            return truncate(step.question) || "Nessuna domanda impostata";

        case FlowStepTypes.TOKEN:
            return truncate(step.tokenLabel) || "Nessuna label impostata";

        case FlowStepTypes.GO_TO:
        case FlowStepTypes.COMPASS:
            if (step.locationLabel) return truncate(step.locationLabel);
            if (step.targetLat && step.targetLng) return `${step.targetLat.toFixed(5)}, ${step.targetLng.toFixed(5)}`;
            return "Nessuna posizione impostata";

        case FlowStepTypes.QR_CODE_SCAN:
            return step.qrCodeId ? "QR collegato" : "⚠ Nessun QR selezionato";

        case FlowStepTypes.RANKING:
            return `Individuali: ${step.showIndividual ? "Sì" : "No"} · Gruppi: ${step.showGroups ? "Sì" : "No"}`;

        case FlowStepTypes.END_GAME:
            return `Mostra punteggio finale: ${step.showFinalScore ? "Sì" : "No"}`;

        case FlowStepTypes.START_GAME:
        case FlowStepTypes.STEP:
        case FlowStepTypes.SEMAFORO:
            return truncate(step.displayDesc) || "Nessuna descrizione";

        default:
            return "";
    }
}

function getHelper(type: FlowStepTypes): { title: string; subtitle: string; icon: ReactElement } {
    switch (type) {
        case FlowStepTypes.START_GAME:
            return {
                title: "Inizio partita",
                subtitle: "Introduce i giocatori all'esperienza e ne spiega le regole",
                icon: <PlayCircleFilledWhite />,
            };

        case FlowStepTypes.QR_CODE_SCAN:
            return {
                title: "Scan QR code",
                subtitle: "Il giocatore deve scansionare un codice QR per procedere",
                icon: <QrCodeScanner />,
            };

        case FlowStepTypes.QUIZ:
            return {
                title: "Risolvi indovinello",
                subtitle: "Un enigma da risolvere per sbloccare il prossimo passo",
                icon: <PsychologyAlt />,
            };

        case FlowStepTypes.TOKEN:
            return {
                title: "Ritira un token",
                subtitle: "Un oggetto fisico o virtuale da collezionare lungo il percorso",
                icon: <WorkspacePremium />,
            };

        case FlowStepTypes.GO_TO:
            return {
                title: "Raggiungi un luogo",
                subtitle: "Il giocatore deve arrivare in una venue specifica per continuare",
                icon: <MapsHomeWork />,
            };

        case FlowStepTypes.COMPASS:
            return {
                title: "Bussola",
                subtitle: "Usa la direzione della bussola per scoprire dove andare",
                icon: <Explore />,
            };

        case FlowStepTypes.RANKING:
            return {
                title: "Classifica",
                subtitle: "Mostra la classifica dei gruppi o giocatori attivi",
                icon: <EmojiEvents />,
            };

        case FlowStepTypes.QUESTION:
            return {
                title: "Domanda a risposta chiusa",
                subtitle: "Il giocatore deve scegliere la risposta corretta",
                icon: <QuestionAnswer />,
            };

        case FlowStepTypes.END_GAME:
            return {
                title: "Fine partita",
                subtitle: "Termina l’esperienza e mostra un messaggio finale",
                icon: <Flag />,
            };

        case FlowStepTypes.STEP:
            return {
                title: "Step normale",
                subtitle: "Inserisci uno step intermedio",
                icon: <Stairs />,
            };

        case FlowStepTypes.SEMAFORO:
            return {
                title: "Semaforo",
                subtitle: "Aspetta gli altri gruppi",
                icon: <Traffic />,
            };

        default:
            throw new Error("Unsupported step type");
    }
}

function EndGameFormFlow({step, onUpdate}: {
    step: EndGame;
    onUpdate?: (updatedStep: EndGame) => void;
}) {
    const handleChange = (field: keyof EndGame, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };

    return (
        <FormControl size="sm">
            <FormLabel>Mostra punteggio finale</FormLabel>
            <Select value={Boolean(step.showFinalScore)}
                    onChange={(e, newValue) => handleChange("showFinalScore", newValue)}
            >
                <Option value={true}>Sì</Option>
                <Option value={false}>No</Option>
            </Select>
        </FormControl>
    )
}
function QrCodePicker({qrCodeId, onSelect}: {
    qrCodeId: string | null | undefined;
    onSelect: (qrCodeId: string) => void;
}) {
    const [options, setOptions] = useState<QrCodeCatalogEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getQrCodes().then(setOptions).finally(() => setLoading(false));
    }, []);

    const handleCreateNew = async () => {
        const created = await createQrCode({});
        if (created) {
            setOptions((prev) => [created, ...prev]);
            onSelect(created.id);
        }
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Select
                placeholder={loading ? "Caricamento…" : "Seleziona un QR fisso"}
                value={qrCodeId || null}
                onChange={(_, newValue) => newValue && onSelect(newValue as string)}
                sx={{minWidth: 260}}
            >
                {options.map((o) => (
                    <Option key={o.id} value={o.id}>
                        {o.label || o.code}{o.lat == null ? " · senza posizione" : ""}
                    </Option>
                ))}
            </Select>
            <Button size="sm" variant="soft" onClick={handleCreateNew}>
                + Nuovo QR (senza posizione)
            </Button>
        </Stack>
    )
}
function QrCodeFormFlow({step, onUpdate}: {
    step: QrCodeScan;
    onUpdate?: (updatedStep: QrCodeScan) => void;
}) {
    const handleChange = (field: keyof QrCodeScan, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };

    return (
        <FormControl size="sm"
                     error={!step.qrCodeId && !step.qrContent}
        >
            <FormLabel>QR fisso</FormLabel>
            <QrCodePicker qrCodeId={step.qrCodeId} onSelect={(id) => handleChange("qrCodeId", id)} />
            {!step.qrCodeId && step.qrContent && (
                <FormHelperText>
                    Step legacy con testo libero "{step.qrContent}" — seleziona un QR fisso per aggiornarlo.
                </FormHelperText>
            )}
        </FormControl>
    )
}
function QuizFormFlow({step, onUpdate}: {step: Quiz, onUpdate?: (updatedStep: Quiz) => void}) {
    const handleChange = (field: keyof Quiz, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };
    return (
        <Stack direction="column" spacing={2}>
            <FormControl size="sm"
                         error={step.question.length < 5}
            >
                <FormLabel>Domanda</FormLabel>
                <Input type="text"
                       placeholder="Domanda"
                       value={step.question}
                       onChange={(e) => handleChange("question", e.target.value)}
                />
            </FormControl>
            <FormControl size="sm" error={!Array.isArray(step.acceptedAnswers) || step.acceptedAnswers.length <= 0}>
                <FormLabel>
                    <Typography
                        endDecorator={
                            <IconButton
                                onClick={() =>
                                    handleChange(
                                        "acceptedAnswers",
                                        [...(step.acceptedAnswers || []), ""]
                                    )
                                }
                            >
                                <AddBox />
                            </IconButton>
                        }
                    >
                        Risposte accettate
                    </Typography>
                </FormLabel>
                <Stack direction="column" spacing={1}>
                    {(Array.isArray(step.acceptedAnswers) ? step.acceptedAnswers : []).map((a, index) => (
                        <Input
                            key={index}
                            type="text"
                            placeholder="Risposta"
                            value={a}
                            onChange={(e) => {
                                const updatedAnswers = [...(step.acceptedAnswers || [])]
                                updatedAnswers[index] = e.target.value
                                handleChange("acceptedAnswers", updatedAnswers)
                            }}
                            endDecorator={
                                <IconButton
                                    onClick={() => {
                                        const updatedAnswers = (step.acceptedAnswers || []).filter((_, i) => i !== index)
                                        handleChange("acceptedAnswers", updatedAnswers)
                                    }}
                                    disabled={step.acceptedAnswers.length <= 1}
                                >
                                    <Delete />
                                </IconButton>
                            }
                        />
                    ))}
                </Stack>
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Ignora maiuscole e minuscole</FormLabel>
                <Select value={Boolean(step.caseInsensitive)}
                        onChange={(e, newValue) => handleChange("caseInsensitive", newValue)}
                >
                    <Option value={true}>Sì</Option>
                    <Option value={false}>No</Option>
                </Select>
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Tipo contenuto multimediale</FormLabel>
                <Select value={step.mediaType}
                        onChange={(e, newValue) => handleChange("mediaType", newValue)}
                >
                    <Option value={MediaTypes.none}>Nessuno</Option>
                    <Option value={MediaTypes.image}>Immagine</Option>
                    <Option value={MediaTypes.video}>Video</Option>
                    <Option value={MediaTypes.audio}>Audio</Option>
                </Select>
            </FormControl>
            {
                step.mediaType !== MediaTypes.none ?
                    <>
                        <FormControl size="sm">
                            <FormLabel>URL</FormLabel>
                            <Input type="url"
                                   placeholder="https://"
                                   value={step.url}
                                   onChange={e => handleChange("url", e.target.value)}
                            />
                        </FormControl>
                        <FormControl size="sm">
                            <FormLabel>Autoplay</FormLabel>
                            <Select value={Boolean(step.autoplay)}
                                    onChange={(e, newValue) => handleChange("autoplay", newValue)}
                            >
                                <Option value={true}>Sì</Option>
                                <Option value={false}>No</Option>
                            </Select>
                        </FormControl>
                    </>
                    : null
            }
        </Stack>
    )
}
function TokenFormFlow({step, onUpdate}: {step: Token, onUpdate?: (updatedStep: Token) => void}) {
    const handleChange = (field: keyof Token, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };
    return (
        <FormControl size="sm"
                     error={step.tokenLabel.length < 5}
        >
            <FormLabel>Label token</FormLabel>
            <Input type="text"
                   placeholder="XXXXXX"
                   value={step.tokenLabel}
                   onChange={(e) => handleChange("tokenLabel", e.target.value)}
            />
        </FormControl>
    )
}
function GoToFormFlow({step, onUpdate}: {step: GoTo, onUpdate?: (updatedStep: GoTo) => void}) {
    const handleChange = (field: keyof GoTo, value: any) => {
        const updated = {...step, [field]: value};
        onUpdate?.(updated);
    };

    const initialPosition = (step.targetLat && step.targetLng) ? new LatLng(step.targetLat, step.targetLng) : null;
    const [position, setPosition] = useState<LatLng | null>(initialPosition);

    const handleCoordChange = (field: "targetLat" | "targetLng", value: string) => {
        const parsed = parseFloat(value);
        handleChange(field, isNaN(parsed) ? null : parsed);

        const lat = field === "targetLat" ? parsed : step.targetLat;
        const lng = field === "targetLng" ? parsed : step.targetLng;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            setPosition(new LatLng(lat, lng));
        }
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                const updated = {...step, targetLat: e.latlng.lat, targetLng: e.latlng.lng};
                onUpdate?.(updated);
                setPosition(e.latlng);
                map.flyTo(e.latlng, map.getZoom());
            },
        });

        return position === null ? null : (
            <Marker position={position}>
                <Popup>Segnaposto</Popup>
            </Marker>
        );
    }

    return (
        <Stack direction="column" spacing={2}>
            <FormControl size="sm">
                <FormLabel>Label posizione</FormLabel>
                <Input
                    type="text"
                    placeholder="Camping Bar"
                    value={step.locationLabel}
                    onChange={e => handleChange("locationLabel", e.target.value)}
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Raggio tolleranza</FormLabel>
                <Input
                    type="number"
                    value={step.radiusMeters}
                    onChange={e => handleChange("radiusMeters", e.target.value)}
                    endDecorator={<Typography>metri</Typography>}
                />
            </FormControl>

            {/* Coordinate manuali */}
            <Stack direction="row" spacing={1}>
                <FormControl size="sm" sx={{flex: 1}}>
                    <FormLabel>Latitudine</FormLabel>
                    <Input
                        type="number"
                        placeholder="45.07049"
                        value={step.targetLat ?? ""}
                        onChange={e => handleCoordChange("targetLat", e.target.value)}
                        slotProps={{input: {step: "any"}}}
                    />
                </FormControl>
                <FormControl size="sm" sx={{flex: 1}}>
                    <FormLabel>Longitudine</FormLabel>
                    <Input
                        type="number"
                        placeholder="7.68682"
                        value={step.targetLng ?? ""}
                        onChange={e => handleCoordChange("targetLng", e.target.value)}
                        slotProps={{input: {step: "any"}}}
                    />
                </FormControl>
            </Stack>

            <div style={{height: '400px'}}>
                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                              className="map-container-leaflet">
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker/>
                </MapContainer>
            </div>
        </Stack>
    );
}

function CompassFormFlow({step, onUpdate}: { step: Compass, onUpdate?: (updatedStep: Compass) => void }) {
    const handleChange = (field: keyof Compass, value: any) => {
        const updated = {...step, [field]: value};
        onUpdate?.(updated);
    };

    const initialPosition = (step.targetLat && step.targetLng) ? new LatLng(step.targetLat, step.targetLng) : null;
    const [position, setPosition] = useState<LatLng | null>(initialPosition);

    const handleCoordChange = (field: "targetLat" | "targetLng", value: string) => {
        const parsed = parseFloat(value);
        handleChange(field, isNaN(parsed) ? null : parsed);

        const lat = field === "targetLat" ? parsed : step.targetLat;
        const lng = field === "targetLng" ? parsed : step.targetLng;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            setPosition(new LatLng(lat, lng));
        }
    };

    function LocationMarker() {
        const map = useMapEvents({
            click(e) {
                const updated = {...step, targetLat: e.latlng.lat, targetLng: e.latlng.lng};
                onUpdate?.(updated);
                setPosition(e.latlng);
                map.flyTo(e.latlng, map.getZoom());
            },
        });

        return position === null ? null : (
            <Marker position={position}>
                <Popup>Segnaposto</Popup>
            </Marker>
        );
    }

    return (
        <Stack direction="column" spacing={2}>
            <FormControl size="sm">
                <FormLabel>Label posizione</FormLabel>
                <Input
                    type="text"
                    placeholder="Camping Bar"
                    value={step.locationLabel}
                    onChange={e => handleChange("locationLabel", e.target.value)}
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Raggio tolleranza</FormLabel>
                <Input
                    type="number"
                    value={step.radiusMeters}
                    onChange={e => handleChange("radiusMeters", e.target.value)}
                    endDecorator={<Typography>metri</Typography>}
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Distanza visibile</FormLabel>
                <Select
                    value={Boolean(step.distanceVisible)}
                    onChange={(e, newValue) => handleChange("distanceVisible", newValue)}
                >
                    <Option value={true}>Sì</Option>
                    <Option value={false}>No</Option>
                </Select>
            </FormControl>

            {/* Coordinate manuali */}
            <Stack direction="row" spacing={1}>
                <FormControl size="sm" sx={{flex: 1}}>
                    <FormLabel>Latitudine</FormLabel>
                    <Input
                        type="number"
                        placeholder="45.07049"
                        value={step.targetLat ?? ""}
                        onChange={e => handleCoordChange("targetLat", e.target.value)}
                        slotProps={{input: {step: "any"}}}
                    />
                </FormControl>
                <FormControl size="sm" sx={{flex: 1}}>
                    <FormLabel>Longitudine</FormLabel>
                    <Input
                        type="number"
                        placeholder="7.68682"
                        value={step.targetLng ?? ""}
                        onChange={e => handleCoordChange("targetLng", e.target.value)}
                        slotProps={{input: {step: "any"}}}
                    />
                </FormControl>
            </Stack>

            <div style={{height: '400px'}}>
                <MapContainer center={[45.07049, 7.68682]} zoom={16} scrollWheelZoom={true}
                              className="map-container-leaflet">
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker/>
                </MapContainer>
            </div>
        </Stack>
    );
}

function RankingFormFlow({step, onUpdate}: { step: Ranking, onUpdate?: (updatedStep: Ranking) => void }) {
    const handleChange = (field: keyof Ranking, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };
    return (
        <Stack direction="column" spacing={2}>
            <FormControl size="sm">
                <FormLabel>Mostra individuali</FormLabel>
                <Select value={Boolean(step.showIndividual)}
                        onChange={(e, newValue) => handleChange("showIndividual", newValue)}
                >
                    <Option value={true}>Sì</Option>
                    <Option value={false}>No</Option>
                </Select>
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Mostra gruppi</FormLabel>
                <Select value={Boolean(step.showGroups)}
                        onChange={(e, newValue) => handleChange("showGroups", newValue)}
                >
                    <Option value={true}>Sì</Option>
                    <Option value={false}>No</Option>
                </Select>
            </FormControl>
        </Stack>
    )
}
function QuestionFormFlow({step, onUpdate}: {step: Question, onUpdate?: (updatedStep: Question) => void}) {
    const handleChange = (field: keyof Question, value: any) => {
        const updated = { ...step, [field]: value };
        onUpdate?.(updated); // 🔹 Notifica al genitore
    };
    return (
        <Stack direction="column" spacing={2}>
            <FormControl size="sm"
                         error={step.question.length < 5}
            >
                <FormLabel>Domanda</FormLabel>
                <Input type="text"
                       placeholder="Domanda"
                       value={step.question}
                       onChange={(e) => handleChange("question", e.target.value)}
                />
            </FormControl>
            <FormControl size="sm" error={!Array.isArray(step.answers) || step.answers.length <= 1}>
                <FormLabel>
                    <Typography
                        endDecorator={
                            <IconButton
                                onClick={() =>
                                    handleChange(
                                        "answers",
                                        [...(step.answers || []), ""]
                                    )
                                }
                            >
                                <AddBox />
                            </IconButton>
                        }
                    >
                        Risposte (la prima è quella corretta)
                    </Typography>
                </FormLabel>
                <Stack direction="column" spacing={1}>
                    {(Array.isArray(step.answers) ? step.answers : []).map((a, index) => (
                        <Input
                            key={index}
                            type="text"
                            placeholder="Risposta"
                            value={a}
                            onChange={(e) => {
                                const updatedAnswers = [...(step.answers || [])]
                                updatedAnswers[index] = e.target.value
                                handleChange("answers", updatedAnswers)
                            }}
                            startDecorator={index === 0 ? <Chip color="success" size="sm" variant="soft" sx={{textTransform: "uppercase"}}>Corretta</Chip> : <Chip color="danger" size="sm" variant="soft" sx={{textTransform: "uppercase"}}>Sbagliata</Chip>}
                            endDecorator={
                                <IconButton
                                    onClick={() => {
                                        const updatedAnswers = (step.answers || []).filter((_, i) => i !== index)
                                        handleChange("answers", updatedAnswers)
                                    }}
                                    disabled={step.answers.length <= 2}
                                >
                                    <Delete />
                                </IconButton>
                            }
                        />
                    ))}
                </Stack>
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Tipo contenuto multimediale</FormLabel>
                <Select value={step.mediaType}
                        onChange={(e, newValue) => handleChange("mediaType", newValue)}
                >
                    <Option value={MediaTypes.none}>Nessuno</Option>
                    <Option value={MediaTypes.image}>Immagine</Option>
                    <Option value={MediaTypes.video}>Video</Option>
                    <Option value={MediaTypes.audio}>Audio</Option>
                </Select>
            </FormControl>
            {
                step.mediaType !== MediaTypes.none ?
                    <>
                        <FormControl size="sm">
                            <FormLabel>URL</FormLabel>
                            <Input type="url"
                                   placeholder="https://"
                                   value={step.url}
                                   onChange={e => handleChange("url", e.target.value)}
                            />
                        </FormControl>
                        <FormControl size="sm">
                            <FormLabel>Autoplay</FormLabel>
                            <Select value={Boolean(step.autoplay)}
                                    onChange={(e, newValue) => handleChange("autoplay", newValue)}
                            >
                                <Option value={true}>Sì</Option>
                                <Option value={false}>No</Option>
                            </Select>
                        </FormControl>
                    </>
                    : null
            }
        </Stack>
    )
}


export function createStepFlow(type: FlowStepTypes): FlowStep {
    const id = getUUID();

    switch (type) {
        case FlowStepTypes.START_GAME:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                hints: [""],
                hintsDelay: 120,
            } as StartGame;

        case FlowStepTypes.QR_CODE_SCAN:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                qrCodeId: null,
                hints: [""],
                hintsDelay: 120,
            } as QrCodeScan;

        case FlowStepTypes.QUIZ:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                question: "",
                acceptedAnswers: [""],
                caseInsensitive: true,
                mediaType: MediaTypes.none,
                url: "",
                autoplay: true,
                hints: [""],
                hintsDelay: 120,
            } as Quiz;

        case FlowStepTypes.TOKEN:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                tokenLabel: "",
                hints: [""],
                hintsDelay: 120,
            } as Token;

        case FlowStepTypes.GO_TO:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                hints: [""],
                hintsDelay: 120,
            } as GoTo;

        case FlowStepTypes.COMPASS:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                distanceVisible: true,
                hints: [""],
                hintsDelay: 120,
            } as Compass;

        case FlowStepTypes.RANKING:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                showGroups: true,
                showIndividual: true,
                hints: [""],
                hintsDelay: 120,
            } as Ranking;

        case FlowStepTypes.QUESTION:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                question: "",
                answers: ["", ""],
                mediaType: MediaTypes.none,
                url: "",
                autoplay: true,
                hints: [""],
                hintsDelay: 120,
            } as Question;

        case FlowStepTypes.END_GAME:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                hints: [""],
                hintsDelay: 120,
            } as EndGame;

        case FlowStepTypes.STEP:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                hints: [""],
                hintsDelay: 120,
            } as Step;

        case FlowStepTypes.SEMAFORO:
            return {
                id,
                type,
                displayTitle: "",
                displayDesc: "",
                hints: [""],
                hintsDelay: 120,
            } as Semaforo;

        default:
            throw new Error("Unsupported step type");
    }
}